import { useEffect, useMemo, useState } from "react"
import { GetApp } from "../../App"
import { GetShipContext } from "../../App/Context"
import { logger } from "../../App/Logger"
import { Block, BlockRow } from "../../Components/Block"
import { Loading } from "../../Components/Loading"
import { Overlay } from "../../Components/Overlay"
import { ShipUserSelectData } from "../../Components/ShipUserSelect"
import { ApiClient, CurrentLogApi, IShipUser } from "../../Types"
import { GetShipUsers } from "../ShipView"
import { GetShipView } from "./Context"

// Data helpers for the "acting ShipUser" picker (#197/#1026 (tracker #827) user switch). Kept
// separate from the component so the eligibility rules can be unit-tested without
// mounting the app contexts.
export const UserSwitchDataOwnership = "fresh-validation-snapshot" as const;

export const UserSwitchViewData = {
  // Eligibility is the currently open Day's crew/skipper roster (both ShipUser ids) --
  // the same roster the day's watch/crew UI already shows. The skipper (if set) is
  // listed first since they're usually the one attributing entries on someone else's
  // behalf.
  async loadEligibleShipUserIds(api: ApiClient): Promise<number[]> {
    const current = await new CurrentLogApi(api).current.day({ params: {} }).call();
    const today = current.days?.days.find((d) => d.day == current.day);
    if (!today) {
      return [];
    }
    return [...(today.skipper != undefined ? [today.skipper] : []), ...(today.crew || [])];
  },
  // Intersects the ship's full crew with today's eligible ids, restricted to active
  // ShipUsers (matches the existing ShipUserSelect convention), sorted by display label.
  eligibleShipUsers(shipUsers: IShipUser[] | undefined, eligibleIds: number[] | undefined): IShipUser[] {
    if (!shipUsers || !eligibleIds) {
      return [];
    }
    return shipUsers
      .filter((user) => user.active > 0 && eligibleIds.includes(user.id))
      .toSorted((a, b) => ShipUserSelectData.userLabel(a).localeCompare(ShipUserSelectData.userLabel(b)));
  },
};

// The Ship-level overlay ("Switch User") that lets whoever is at this device -- primary
// or companion, same mechanism -- pick a different crew member to attribute new
// log/note entries to, without a full logout/login (#197). Selecting a user only ever
// changes attribution (Client's static acting-ship-user header); it never changes the
// permission ceiling, which always stays keyed to the real, authenticated ShipUser
// (ShipContext.shipUser) -- see requireActingShipUserId server-side. Picking the real,
// authenticated user again clears the override; the "Return to original user" menu
// action (MenuAccount) offers the same reset without opening this overlay.
export function UserSwitchView() {
  const app = GetApp();
  if (!app.hasFeature("multi-user")) {
    return null;
  }
  return <EnabledUserSwitchView />;
}

function EnabledUserSwitchView() {
  const app = GetApp();
  const t = app.getT("UserSwitchView");
  const { shipUser: realShipUser } = GetShipContext();
  const shipUsers = GetShipUsers();
  const { actingShipUser, setActingShipUser, resetShipOverlay } = GetShipView();
  const [eligibleIds, setEligibleIds] = useState<number[] | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    UserSwitchViewData.loadEligibleShipUserIds(app.apiClient).then((ids) => {
      if (!cancelled) {
        setEligibleIds(ids);
      }
    }).catch((error) => logger.error("UserSwitchView load eligible users", error));
    return () => { cancelled = true; };
  }, []);

  const eligibleUsers = useMemo(() => UserSwitchViewData.eligibleShipUsers(shipUsers, eligibleIds), [shipUsers, eligibleIds]);

  const selectUser = (user: IShipUser) => {
    setActingShipUser(user.id == realShipUser?.id ? undefined : user);
    resetShipOverlay();
  };

  return (
    <Overlay className="UserSwitchView" closeCallback={resetShipOverlay} title={t("title")}>
      <div className="Overlay-body">
        <Block>
          {eligibleIds == undefined && (
            <BlockRow><Loading /></BlockRow>
          )}
          {eligibleIds != undefined && eligibleUsers.length == 0 && (
            <BlockRow>{t("empty")}</BlockRow>
          )}
          {eligibleUsers.map((user) => (
            <BlockRow key={user.id}>
              <button
                type="button"
                className={"button UserSwitchView-user" + (actingShipUser?.id == user.id ? " active" : "")}
                onClick={() => selectUser(user)}
              >
                {ShipUserSelectData.userLabel(user)}
              </button>
            </BlockRow>
          ))}
        </Block>
      </div>
    </Overlay>
  );
}
